<template>
  <div class="locations-tab">
    <b-form @submit.prevent="onSubmit" @reset.prevent="resetForm">
      <b-card class="p-1" no-body>
        <!-- Header with add button  -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Coordonnées de Géolocalisation</h5>
          <b-button
              size="sm"
              variant="primary"
              @click="addNewLocation"
          >
            <feather-icon class="mr-1" icon="PlusIcon"/>
            Ajouter une adresse
          </b-button>
        </div>

        <!--addresses list -->
        <b-tabs pills vertical>
          <b-tab
              v-for="(location, index) in locations"
              :key="location.id"
              :active="index === activeTab"
              @click="activeTab = index"
          >
            <!-- Tab title -->
            <template #title>
              <div class="d-flex align-items-center">
                <span>Adresse {{ index + 1 }}</span>
                <feather-icon
                    v-if="locations.length > 1"
                    class="ml-1 cursor-pointer"
                    icon="TrashIcon"
                    size="14"
                    @click.stop="removeLocation(index)"
                />
              </div>
            </template>

            <!-- tab content -->
            <b-card border-variant="light">
              <b-row class="mb-2">
                <b-col cols="12" md="4">
                  <b-form-group
                      :label-for="`coordinate-type-${location.id}`"
                      label="Type de coordonnées"
                  >
                    <v-select
                        v-model="location.coordinate_type"
                        :clearable="false"
                        :input-id="`coordinate-type-${location.id}`"
                        :options="coordinateTypeOptions"
                        :reduce="type => type.value"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Champs conditionnels selon le type -->
              <div class="coordinate-fields">
                <!-- UTM Fields -->
                <b-row v-show="location.coordinate_type === 'utm'">
                  <b-col md="4">
                    <b-form-group label="Zone UTM">
                      <b-form-input
                          v-model="location.marker_data.utm_zone"
                          placeholder="Ex: 31N"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="4">
                    <b-form-group label="Est">
                      <b-form-input
                          v-model.number="location.marker_data.utm_easting"
                          placeholder="Ex: 430186"
                          type="number"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="4">
                    <b-form-group label="Nord">
                      <b-form-input
                          v-model.number="location.marker_data.utm_northing"
                          placeholder="Ex: 738055"
                          type="number"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- DMS Fields -->
                <b-row v-show="location.coordinate_type === 'dms'">
                  <b-col md="6">
                    <b-form-group label="Latitude">
                      <b-form-input
                          v-model="location.marker_data.latitude"
                          placeholder="Ex: 6º47'42.02"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="Longitude">
                      <b-form-input
                          v-model="location.marker_data.longitude"
                          placeholder="Ex: 2º3'33.55"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- DD Fields -->
                <b-row v-show="location.coordinate_type === 'dd'">
                  <b-col md="6">
                    <b-form-group label="Latitude">
                      <b-form-input
                          v-model.number="location.marker_data.latitude"
                          placeholder="Ex: 6.474232"
                          step="0.000001"
                          type="number"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="Longitude">
                      <b-form-input
                          v-model.number="location.marker_data.longitude"
                          placeholder="Ex: 2.387855"
                          step="0.000001"
                          type="number"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Carte -->
                <b-row class="mt-2">
                  <b-col cols="12">
                    <single-map
                        :address.sync="location.coordinate_address"
                        :latitude="location.marker_data.latitude"
                        :longitude="location.marker_data.longitude"
                        :type="location.coordinate_type"
                        :utm_easting="location.marker_data.utm_easting"
                        :utm_northing="location.marker_data.utm_northing"
                        :utm_zone="location.marker_data.utm_zone"
                    />
                  </b-col>
                </b-row>
              </div>
            </b-card>
          </b-tab>
        </b-tabs>

      </b-card>
      <b-button
          :block="$store.getters['app/currentBreakPoint'] === 'xs'"
          :disabled="isLoading"
          class="mr-0 mr-sm-1"
          type="submit"
          variant="primary"
      >
        <b-spinner v-if="isLoading" small></b-spinner>
        Sauvegarder
      </b-button>
      <b-button
          :block="$store.getters['app/currentBreakPoint'] === 'xs'"
          variant="outline-secondary"
          @click="() => $router.go(-1)"
      >
        Retour
      </b-button>
    </b-form>
  </div>
</template>

<script>
import {BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BRow, BSpinner, BTab, BTabs} from 'bootstrap-vue'
import vSelect from "vue-select";
import SingleMap from "@/views/apps/project/maps/SingleMap.vue";
import store from "@/store";
import router from "@/router";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: 'ProjectEditTabGeolocation',
  components: {
    BSpinner,
    BFormInput,
    BCard,
    BTabs,
    BTab,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    vSelect,
    SingleMap
  },
  props: {
    projectData: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      isLoading: false,
      activeTab: 0,
      locations: [
        {
          id: Date.now(),
          coordinate_type: 'dd',
          marker_data: {
            latitude: null,
            longitude: null,
            utm_zone: '',
            utm_easting: null,
            utm_northing: null
          },
          coordinate_address: ''
        }
      ],
      coordinateTypeOptions: [
        {label: 'Degrés décimaux', value: 'dd'},
        {label: 'Degrés/Minutes/Secondes', value: 'dms'},
        {label: 'UTM', value: 'utm'}
      ]
    }
  },
  mounted() {
    this.resetForm();
  },
  methods: {
    defaultLocationFields() {
      return {
        coordinate_type: 'dd',
        marker_data: {
          latitude: null,
          longitude: null,
          utm_zone: '',
          utm_easting: null,
          utm_northing: null
        },
        coordinate_address: ''
      }
    },
    addNewLocation() {
      let newLocation = this.defaultLocationFields();
      newLocation.id = Date.now();
      this.locations.push(newLocation)
      this.activeTab = this.locations.length - 1
    },
    removeLocation(index) {
      this.locations.splice(index, 1)
      if (this.activeTab >= this.locations.length) {
        this.activeTab = this.locations.length - 1
      }
    },
    onSubmit() {
      this.isLoading = true;

      let invalidLocations = this.locations.filter(location => !location.coordinate_address.trim());

      if (invalidLocations.length > 0) {
        this.isLoading = false;
        this.showToast(
            "Adresses invalides. Veuillez vérifier les addresses des toutes les coordonnées.",
            "CoffeeIcon",
            "error"
        );
        return;
      }
      const projectLocal = {
        ...this.projectData,
        customer: typeof this.projectData.customer === "object"
          ? this.projectData.customer?.id
          : this.projectData.customer,
        locations: this.locations
      };

      store.dispatch('project/updateProject', projectLocal)
          .then(() => {
            router.go(-1);
          }).catch(() => this.isLoading = false);
    },
    resetForm() {
      this.isLoading = false;
      this.activeTab = 0;
      let projectLocations = this.projectData?.locations;
      if (projectLocations.length > 0) {
        this.locations = projectLocations.map(location => ({
          ...location,
          id: Date.now()
        }));
      } else {
        this.locations = [
          {
            id: Date.now(),
            ...this.defaultLocationFields()
          }
        ]
      }
    },
    showToast(message, icon, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: icon,
          variant: variant,
        },
      });
    },
  }
}
</script>

<style lang="scss" scoped>
.locations-tab {
  .nav-pills {
    min-width: 200px;
  }
}
</style>